import React, { useEffect, useState, useCallback } from 'react';
import { Search, Eye, X, ChevronRight, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const STATUS_OPTIONS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-800',
};

function OrderDetailModal({ order, onClose, onUpdated }: { order: any; onClose: () => void; onUpdated: () => void; }) {
  const [status, setStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '');
  const [loading, setLoading] = useState(false);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/orders/${order.id}`, { status, tracking_number: trackingNumber, admin_notes: adminNotes });
      toast.success('Order updated!');
      onUpdated();
      onClose();
    } catch { toast.error('Failed to update order'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Order: {order.order_number}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{new Date(order.created_at).toLocaleString('en-PK')}</p>
          </div>
          <button aria-label="Close" title="Close" onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">{order.first_name} {order.last_name}</p>
              <p className="text-xs text-gray-600">{order.email}</p>
              <p className="text-xs text-gray-600">{order.phone}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Shipping Address</p>
              <p className="text-sm text-gray-700 mt-1">{order.address}</p>
              <p className="text-xs text-gray-600">{order.city}, {order.state}</p>
              <p className="text-xs text-gray-600">{order.country}</p>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Order Items</h3>
            <div className="space-y-3">
              {order.items?.map((item: any) => (
                <div key={item.id} className="flex items-center gap-3 bg-gray-50 p-3 rounded">
                  {item.product_image && <img src={getImageUrl(item.product_image)} alt={item.product_name} className="w-12 h-14 object-cover rounded" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                    {item.variant_info && <p className="text-xs text-gray-500">{item.variant_info}</p>}
                    <p className="text-xs text-gray-500">Qty: {item.quantity} × Rs.{Number(item.unit_price).toLocaleString()}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">Rs.{Number(item.total_price).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
            <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>Rs.{Number(order.subtotal).toLocaleString()}</span></div>
            <div className="flex justify-between text-gray-600"><span>Shipping</span><span>{Number(order.shipping_cost) === 0 ? 'FREE' : `Rs.${Number(order.shipping_cost).toLocaleString()}`}</span></div>
            <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2"><span>Total</span><span>Rs.{Number(order.total).toLocaleString()}</span></div>
          </div>

          {/* Update Status */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Order Status</label>
              <select title="Order Status" aria-label="Order Status" value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Tracking Number</label>
              <input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="e.g., TCS123456" className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Admin Notes</label>
              <textarea aria-label="Admin Notes" placeholder="Internal notes about this order..." value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={2} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 resize-none" />
            </div>
          </div>

          <div className="flex justify-between gap-3">
            <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-sm font-medium hover:border-gray-900 transition-colors">Cancel</button>
            <button onClick={handleUpdate} disabled={loading} className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-60">
              {loading ? 'Saving...' : 'Update Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const handleDelete = async (id: number, orderNum: string) => {
    if (!window.confirm(`Delete order ${orderNum}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success('Order deleted');
      fetchOrders(pagination.current_page);
    } catch { toast.error('Failed to delete order'); }
  };
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const fetchOrders = useCallback((page = 1) => {
    setLoading(true);
    const params: any = { page };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    api.get('/admin/orders', { params })
      .then(r => {
        setOrders(r.data.data || r.data);
        if (r.data.meta) setPagination({ current_page: r.data.meta.current_page, last_page: r.data.meta.last_page, total: r.data.meta.total });
        else setPagination({ current_page: r.data.current_page || 1, last_page: r.data.last_page || 1, total: r.data.total || 0 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { fetchOrders(1); }, [statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} total orders</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchOrders(1)} placeholder="Search by order #, email, phone..." className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <select title="Filter by status" aria-label="Filter by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-gray-900 bg-white min-w-32">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <button onClick={() => fetchOrders(1)} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors">Filter</button>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? Array(8).fill(null).map((_, i) => (
                <tr key={i}><td colSpan={7} className="px-6 py-4"><div className="h-8 bg-gray-100 animate-pulse rounded" /></td></tr>
              )) : orders.map(order => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-gray-900">{order.order_number}</p>
                    <p className="text-xs text-gray-400">{order.items?.length || 0} item(s)</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{order.first_name} {order.last_name}</p>
                    <p className="text-xs text-gray-500">{order.phone}</p>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">Rs.{Number(order.total).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-xs font-medium text-gray-700 uppercase">{order.payment_method}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{order.payment_status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>{order.status}</span>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString('en-PK')}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button title="View order" onClick={() => setSelectedOrder(order)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                        <Eye size={16} />
                      </button>
                      <button title="Delete order" onClick={() => handleDelete(order.id, order.order_number)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.last_page > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => fetchOrders(page)} className={`w-9 h-9 text-xs font-medium border ${page === pagination.current_page ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-900'}`}>{page}</button>
            ))}
          </div>
        )}
      </div>

      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onUpdated={fetchOrders} />}
    </div>
  );
}
